import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';
import { Avatar, Badge, Button } from '@evogard/ui';
import { count } from '@evogard/domain';
import type { TeamDiagramNode } from '@evogard/contract';

/**
 * O diagrama do time — POR PESSOA, da esquerda para a direita, num painel que
 * se move, aproxima e gira. Pedido do responsável em 23/09/2026.
 *
 * ## Uma caixa por pessoa
 *
 * O líder do time escolhido fica à esquerda, e uma linha sai dele para cada
 * integrante. Quem lidera outro time tem os integrantes DAQUELE time saindo
 * dele, e assim por diante — "Time Meteoro, líder Fernando, daí duas linhas:
 * para a Lara e para a Maria, daí a Lara, e por aí vai". É a mesma regra que
 * forma a cadeia de superiores: quem está no time responde ao líder dele.
 *
 * ## Estrutura, e não números
 *
 * O diagrama responde "quem está sob quem". Quanto cada um vendeu é a tabela, e
 * repetir os números aqui daria duas leituras do mesmo mês para manter de
 * acordo.
 *
 * ## O painel
 *
 * Arrastar move (mouse, caneta ou dedo — são eventos de ponteiro), a roda do
 * mouse e os botões aproximam e afastam, "Girar" roda 90° e "Centralizar"
 * volta ao começo. Com o painel em foco, as setas movem, `+`/`-` aproximam e
 * `0` centraliza — o teclado alcança tudo o que o ponteiro alcança.
 *
 * A roda é escutada com `passive: false`, e é de propósito: o React registra a
 * roda como passiva, e o `preventDefault` dele seria ignorado — o zoom e a
 * rolagem da página aconteceriam juntos.
 *
 * ## As linhas são bordas, e as cores são do tema
 *
 * O organograma deitado: a lista de filhos tem um traço horizontal que sai da
 * caixa da esquerda, e cada filho tem meia barra vertical de cada lado — o
 * primeiro não tem a de cima, o último não tem a de baixo. Tudo com
 * `border-borda-forte`, que mantém o desenho certo no claro e no escuro. A
 * marcação é uma lista aninhada (`ul`/`li`): é a árvore que o leitor de tela
 * anuncia.
 *
 * ## Ocultar o ramo de baixo (13/10/2026)
 *
 * Toda caixa com gente abaixo ganha "Ocultar abaixo"/"Mostrar", e a barra
 * ganha "Recolher tudo" e "Expandir tudo". O ramo oculto SAI da marcação — não
 * é escondido por CSS —, e é isso que reposiciona o resto: a lista de irmãos se
 * fecha sobre o espaço que ele ocupava, e as caixas que um ramo comprido
 * afastava passam a ficar lado a lado. O botão diz quantas pessoas o ramo
 * oculto tem, para que recolher não leia como "esta pessoa não tem ninguém".
 *
 * A raiz nunca recolhe: ocultar tudo abaixo dela deixaria uma caixa sozinha. O
 * estado é do diagrama aberto — abrir outro time começa expandido, pelo `key`
 * que a tela passa.
 */
export function TeamDiagram({
  root,
  onOpenTeam,
  back,
}: {
  root: TeamDiagramNode;
  /** Abre o painel de um time da árvore — todos estão no alcance de quem olha. */
  onOpenTeam: (teamId: number) => void;
  /**
   * Voltar um nível na trilha, e o nome do time para onde se volta. Ausente no
   * time do topo: ali não há de onde se veio, e a caixa do líder fica sem botão.
   */
  back?: { label: string; onBack: () => void };
}) {
  const collapse = useCollapse();
  return (
    <CollapseContext.Provider value={collapse}>
      <Viewport label={`Diagrama de ${root.team.nome}`} collapse={collapse}>
        <ul className="flex" aria-label={`Diagrama de ${root.team.nome}`}>
          <PersonBranch
            branchKey="raiz"
            name={root.leader?.nome ?? null}
            leadsTeam={root}
            onOpenTeam={onOpenTeam}
            back={back}
            root
          />
        </ul>
      </Viewport>
    </CollapseContext.Provider>
  );
}

/**
 * O diagrama GERAL — a base na raiz, e os times de nível 1 saindo dela
 * (28/09/2026). "Evogard Ceará" à esquerda; de cada time de nível 1 sai o
 * líder dele, e daí para baixo é o mesmo desenho do diagrama de um time.
 */
export function TenantDiagram({
  tenantName,
  teams,
  onOpenTeam,
}: {
  tenantName: string;
  /** As árvores dos times de nível 1, na ordem em que o desenho as mostra. */
  teams: readonly TeamDiagramNode[];
  onOpenTeam: (teamId: number) => void;
}) {
  const collapse = useCollapse();
  return (
    <CollapseContext.Provider value={collapse}>
      <Viewport label={`Diagrama geral de ${tenantName}`} collapse={collapse}>
        <ul className="flex" aria-label={`Diagrama geral de ${tenantName}`}>
          <li className="relative flex items-center py-2">
            <section
              aria-label={tenantName}
              className="flex w-52 shrink-0 flex-col items-center gap-2 rounded-borda border border-borda-forte bg-superficie p-3 text-center"
            >
              <span className="rotulo text-texto-suave">Raiz</span>
              <Avatar nome={tenantName} size="md" />
              <span className="text-sm font-medium text-texto">{tenantName}</span>
            </section>
            {teams.length > 0 && (
              <ul
                className="relative flex flex-col pl-6 before:absolute before:left-0 before:top-1/2 before:w-6 before:border-t before:border-borda-forte"
                aria-label={`Os times de nível 1 de ${tenantName}`}
              >
                {teams.map((team) => (
                  <PersonBranch
                    key={team.team.id}
                    branchKey={`time-${team.team.id}`}
                    name={team.leader?.nome ?? null}
                    leadsTeam={team}
                    onOpenTeam={onOpenTeam}
                  />
                ))}
              </ul>
            )}
          </li>
        </ul>
      </Viewport>
    </CollapseContext.Provider>
  );
}

/* --------------------------------------------------------------------------- *
 * Ocultar e mostrar ramos
 * --------------------------------------------------------------------------- */

/**
 * O que está recolhido: um padrão para o diagrama inteiro, e a escolha de cada
 * caixa por cima dele. "Recolher tudo" e "Expandir tudo" trocam o padrão e
 * esquecem as escolhas — senão a caixa aberta à mão continuaria aberta depois
 * de "Recolher tudo", e o botão pareceria não funcionar.
 */
interface Collapse {
  isCollapsed: (key: string) => boolean;
  toggle: (key: string) => void;
  setAll: (collapsed: boolean) => void;
}

function useCollapse(): Collapse {
  const [state, setState] = useState<{
    all: boolean;
    each: ReadonlyMap<string, boolean>;
  }>({ all: false, each: new Map() });

  return {
    isCollapsed: (key) => state.each.get(key) ?? state.all,
    toggle: (key) =>
      setState((s) => {
        const each = new Map(s.each);
        each.set(key, !(s.each.get(key) ?? s.all));
        return { all: s.all, each };
      }),
    setAll: (all) => setState({ all, each: new Map() }),
  };
}

const CollapseContext = createContext<Collapse | null>(null);

/* --------------------------------------------------------------------------- *
 * O painel: mover, aproximar, girar
 * --------------------------------------------------------------------------- */

const MIN_SCALE = 0.3;
const MAX_SCALE = 2.5;
const STEP = 1.2;
const PAN = 48;

interface View {
  x: number;
  y: number;
  scale: number;
  rotation: number;
}

const START: View = { x: 0, y: 0, scale: 1, rotation: 0 };

function clampScale(scale: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

function Viewport({
  label,
  collapse,
  children,
}: {
  label: string;
  collapse: Collapse;
  children: ReactNode;
}) {
  const [view, setView] = useState<View>(START);
  const frame = useRef<HTMLDivElement>(null);
  const drag = useRef<{ id: number; x: number; y: number } | null>(null);

  /*
   * Tela cheia pela API do navegador, sobre a barra E o painel — os controles
   * vão junto. O Esc sai por conta do navegador, e é por isso que o estado é
   * lido do `fullscreenchange`, e não do clique: sair pelo Esc não passa pelo
   * botão, e o rótulo dele ficaria dizendo "Sair da tela cheia" fora dela.
   */
  const shell = useRef<HTMLDivElement>(null);
  const [fullscreen, setFullscreen] = useState(false);
  useEffect(() => {
    const sync = () => setFullscreen(document.fullscreenElement === shell.current);
    /*
     * O navegador já sai com o Esc; este é o cinto junto do suspensório, para
     * o ambiente que entrega a tecla à página em vez de consumi-la.
     */
    const onEscape = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape' && document.fullscreenElement === shell.current) {
        void document.exitFullscreen();
      }
    };
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('keydown', onEscape);
    };
  }, []);
  const toggleFullscreen = () => {
    if (document.fullscreenElement === shell.current) {
      void document.exitFullscreen();
    } else {
      /* Ao entrar, o foco vai ao painel: as setas e o + / - já funcionam. */
      void shell.current
        ?.requestFullscreen()
        .then(() => frame.current?.focus())
        .catch(() => undefined);
    }
  };

  const zoom = (factor: number) =>
    setView((v) => ({ ...v, scale: clampScale(v.scale * factor) }));

  /* A roda, com `passive: false` — ver o cabeçalho do arquivo. */
  useEffect(() => {
    const element = frame.current;
    if (element === null) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      setView((v) => ({
        ...v,
        scale: clampScale(v.scale * (e.deltaY < 0 ? STEP : 1 / STEP)),
      }));
    };
    element.addEventListener('wheel', onWheel, { passive: false });
    return () => element.removeEventListener('wheel', onWheel);
  }, []);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    /* O clique num botão da caixa ("Abrir") é clique, e não começo de arrasto. */
    if ((e.target as HTMLElement).closest('button') !== null) return;
    drag.current = { id: e.pointerId, x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const start = drag.current;
    if (start === null || start.id !== e.pointerId) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    drag.current = { id: e.pointerId, x: e.clientX, y: e.clientY };
    setView((v) => ({ ...v, x: v.x + dx, y: v.y + dy }));
  };
  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    if (drag.current?.id === e.pointerId) drag.current = null;
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [PAN, 0],
      ArrowRight: [-PAN, 0],
      ArrowUp: [0, PAN],
      ArrowDown: [0, -PAN],
    };
    const move = moves[e.key];
    if (move !== undefined) {
      e.preventDefault();
      setView((v) => ({ ...v, x: v.x + move[0], y: v.y + move[1] }));
    } else if (e.key === '+' || e.key === '=') {
      e.preventDefault();
      zoom(STEP);
    } else if (e.key === '-') {
      e.preventDefault();
      zoom(1 / STEP);
    } else if (e.key === '0') {
      e.preventDefault();
      setView(START);
    }
  };

  return (
    <div
      ref={shell}
      className={`flex flex-col gap-3 ${fullscreen ? 'h-full bg-fundo p-4' : ''}`}
    >
      <div
        className="flex flex-wrap items-center gap-2"
        role="toolbar"
        aria-label="Controles do diagrama"
      >
        <Button
          variant={fullscreen ? 'primario' : 'contorno'}
          size="sm"
          icon={fullscreen ? 'fechar' : 'expandir'}
          onClick={toggleFullscreen}
          aria-pressed={fullscreen}
          title={fullscreen ? 'Sair da tela cheia (Esc)' : 'Tela cheia'}
        >
          {fullscreen ? 'Sair da tela cheia (Esc)' : 'Tela cheia'}
        </Button>
        <Button variant="contorno" size="sm" icon="mais" onClick={() => zoom(STEP)}>
          Aproximar
        </Button>
        <Button variant="contorno" size="sm" icon="menos" onClick={() => zoom(1 / STEP)}>
          Afastar
        </Button>
        <Button
          variant="contorno"
          size="sm"
          icon="atualizar"
          onClick={() => setView((v) => ({ ...v, rotation: (v.rotation + 90) % 360 }))}
        >
          Girar
        </Button>
        <Button
          variant="discreto"
          size="sm"
          icon="expandir"
          onClick={() => setView(START)}
        >
          Centralizar
        </Button>
        {/*
         * Recolher encolhe o desenho, e o deslocamento do arrasto ficaria
         * apontando para onde a árvore comprida estava: volta ao centro junto.
         */}
        <Button
          variant="discreto"
          size="sm"
          icon="seta-esquerda"
          onClick={() => {
            collapse.setAll(true);
            setView((v) => ({ ...v, x: 0, y: 0 }));
          }}
        >
          Recolher tudo
        </Button>
        <Button
          variant="discreto"
          size="sm"
          icon="seta-direita"
          onClick={() => collapse.setAll(false)}
        >
          Expandir tudo
        </Button>
        <span className="text-xs tabular-nums text-texto-suave" aria-live="polite">
          {Math.round(view.scale * 100)}% · {view.rotation}°
        </span>
      </div>

      {/*
       * O painel é FOCÁVEL, e o foco é o que liga o teclado. `touch-action:
       * none` entrega o arrasto do dedo ao diagrama em vez de rolar a página.
       */}
      <div
        ref={frame}
        tabIndex={0}
        role="region"
        aria-label={`${label} — arraste para mover, use a roda ou + e - para aproximar, 0 para centralizar`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={onKeyDown}
        className={`relative ${fullscreen ? 'min-h-0 flex-1' : 'h-[70vh] min-h-[24rem]'} cursor-grab touch-none overflow-hidden rounded-borda border border-borda bg-superficie-suave select-none active:cursor-grabbing focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foco`}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            style={{
              transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale}) rotate(${view.rotation}deg)`,
            }}
            className="origin-center"
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------------- *
 * A árvore, deitada
 * --------------------------------------------------------------------------- */

/**
 * Uma pessoa e quem sai dela, à direita.
 *
 * `leadsTeam` é o time que esta pessoa lidera, se lidera algum: é dele que saem
 * as linhas, uma por integrante. Quem não lidera nada é folha.
 *
 * As linhas: `before` é a meia barra de CIMA mais o traço que chega na caixa,
 * `after` a meia barra de BAIXO. A raiz não tem nada à esquerda, e o primeiro e
 * o último filho perdem a meia barra que sairia para fora do grupo.
 */
function PersonBranch({
  branchKey,
  name,
  leadsTeam,
  onOpenTeam,
  back,
  root = false,
}: {
  /** Identifica a caixa no estado de recolhido — o caminho até ela, único no diagrama. */
  branchKey: string;
  /** `null` na raiz de um time sem líder. */
  name: string | null;
  leadsTeam: TeamDiagramNode | null;
  onOpenTeam: (teamId: number) => void;
  /** Só na raiz: o "Voltar" que ocupa o lugar do "Abrir". */
  back?: { label: string; onBack: () => void };
  root?: boolean;
}) {
  const connectors = root
    ? ''
    : [
        'before:absolute before:left-0 before:top-0 before:h-1/2 before:w-6 before:border-b before:border-l before:border-borda-forte',
        'after:absolute after:left-0 after:top-1/2 after:h-1/2 after:w-6 after:border-l after:border-borda-forte',
        'first:before:border-l-0 last:after:border-l-0',
      ].join(' ');

  const below = leadsTeam?.members ?? [];
  const collapse = useContext(CollapseContext)!;
  const collapsible = !root && leadsTeam !== null && below.length > 0;
  const collapsed = collapsible && collapse.isCollapsed(branchKey);

  return (
    <li className={`relative flex items-center py-2 ${root ? '' : 'pl-6'} ${connectors}`}>
      <section
        aria-label={name ?? `${leadsTeam?.team.nome ?? 'Time'} sem líder`}
        className="flex w-52 shrink-0 flex-col items-center gap-2 rounded-borda border border-borda bg-superficie p-3 text-center"
      >
        {root && leadsTeam !== null && (
          <span className="rotulo text-texto-suave">{leadsTeam.team.nome}</span>
        )}
        {name === null ? (
          <span className="text-sm text-texto-suave">Sem líder</span>
        ) : (
          <>
            <Avatar nome={name} size="md" />
            <span className="text-sm font-medium text-texto">{name}</span>
          </>
        )}
        {leadsTeam !== null && (
          <div className="flex flex-wrap items-center justify-center gap-1.5">
            <Badge tone="informacao">
              {root
                ? 'Líder'
                : name === null
                  ? leadsTeam.team.nome
                  : `lidera ${leadsTeam.team.nome}`}
            </Badge>
            {/*
             * Na RAIZ, "Abrir" abriria o time que já está aberto. O lugar dele é
             * do "Voltar", que sobe um nível na trilha — e no time do topo não
             * há nada: nem de onde se veio, nem o que abrir.
             */}
            {root ? (
              back !== undefined && (
                <Button
                  variant="discreto"
                  size="sm"
                  icon="seta-esquerda"
                  onClick={back.onBack}
                  aria-label={`Voltar para ${back.label}`}
                >
                  Voltar
                </Button>
              )
            ) : (
              <Button
                variant="discreto"
                size="sm"
                onClick={() => onOpenTeam(leadsTeam.team.id)}
                aria-label={`Abrir o painel de ${leadsTeam.team.nome}`}
              >
                Abrir
              </Button>
            )}
          </div>
        )}
        {collapsible && (
          <Button
            variant="discreto"
            size="sm"
            icon={collapsed ? 'seta-direita' : 'seta-esquerda'}
            aria-expanded={!collapsed}
            onClick={() => collapse.toggle(branchKey)}
            aria-label={`${collapsed ? 'Mostrar' : 'Ocultar'} quem responde a ${name ?? leadsTeam.team.nome}`}
          >
            {collapsed
              ? `Mostrar ${count(peopleBelow(leadsTeam), 'pessoa', 'pessoas')}`
              : 'Ocultar abaixo'}
          </Button>
        )}
      </section>

      {below.length > 0 && !collapsed && (
        <ul
          className="relative flex flex-col pl-6 before:absolute before:left-0 before:top-1/2 before:w-6 before:border-t before:border-borda-forte"
          aria-label={`Quem responde a ${name ?? 'este time'}`}
        >
          {below.map((m) => (
            <PersonBranch
              key={m.consultant.id}
              branchKey={`${branchKey}/${m.consultant.id}`}
              name={m.consultant.user.nome}
              leadsTeam={ledBy(leadsTeam!, m.consultant.user.id)}
              onOpenTeam={onOpenTeam}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * O time que um integrante de `team` lidera, procurado PELO LÍDER, e não só por
 * `leadsTeamId`: quem lidera dois times filhos teria um deles fora do desenho,
 * em silêncio. Com mais de um, os integrantes dos dois saem da mesma pessoa —
 * é a quem todos eles respondem.
 */
function ledBy(team: TeamDiagramNode, userId: string): TeamDiagramNode | null {
  const led = team.children.filter((c) => c.leader?.id === userId);
  if (led.length === 0) return null;
  if (led.length === 1) return led[0]!;
  return {
    team: { id: led[0]!.team.id, nome: led.map((c) => c.team.nome).join(' e ') },
    leader: led[0]!.leader,
    members: led.flatMap((c) => c.members),
    children: led.flatMap((c) => c.children),
  };
}

/** Quantas caixas um ramo recolhido esconde — a mesma descida do desenho. */
function peopleBelow(team: TeamDiagramNode): number {
  return team.members.reduce((total, m) => {
    const sub = ledBy(team, m.consultant.user.id);
    return total + 1 + (sub === null ? 0 : peopleBelow(sub));
  }, 0);
}
